import _ from 'lodash';

import { equipmentPrettyType } from 'Utils/filters.js';
import template from './siteManagement.modal.component.html';

/* @ngInject */
function controller(
  $uibModalInstance,
  site,
  EquipmentInfoService,
  RcuInfoService,
  SiteService,
  GroupService,
  GroupsButtonService,
  uiGridConstants,
  growl,
  $scope
) {
  const vm = this;
  vm.title = 'New Site';
  vm.siteName = '';
  vm.siteRegion = '';
  vm.listOfEquipment = [];
  vm.listOfRcus = [];
  vm.siteBorder = null;
  vm.listOfSites = [];
  vm.masterList = [];
  vm.selectedEquipment = [];
  vm.selectedGroups = [];
  vm.groupList = [];

  vm.$onInit = initialize; // $postLink will not call on components not used as directives

  vm.editing = Boolean(site);

  if (vm.editing) {
    vm.title = `Editing ${site.name}`;
    vm.siteName = site.name;
    vm.siteRegion = site.region;
    vm.siteLat = site.latitude;
    vm.siteLng = site.longitude;
    vm.siteBorder = site.border;
    vm.jsonSiteInfo = angular.toJson(site.info, true);
    vm.updatedOrg = site.org;
    vm.org = site.org;

    // Fill in the equipment and rcu fields with the corresponding sites
    EquipmentInfoService.getFullEquipment(site.equipment)
      .then((foundEquipment) => {
        vm.selectedEquipment = foundEquipment;
        RcuInfoService.filterRcusById(site.rcus)
          .then((foundRcus) => {
            vm.selectedEquipment = [...vm.selectedEquipment, ...foundRcus];
          });
      });
  }
  // setup ui grid
  vm.equipmentGrid = {
    minRowsToShow: 6,
    enableFiltering: true,
    enableRowSelection: true,
    enableFullRowSelection: true,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    columnDefs: [{
      field: 'name',
      sort: {
        direction: uiGridConstants.ASC,
        priority: 1
      },
      // Natural sort (to handle eg "Thing 10" > "Thing 2")
      sortingAlgorithm: (a, b) => a.localeCompare(b, undefined, { numeric: true }),

      cellTooltip: true,
      type: 'string',
      enableHiding: false,
    }, {
      field: 'equipmentType',
      cellFilter: 'equipmentPrettyType',
      displayName: 'Type',
      cellTooltip: true,
      enableHiding: false,
      type: 'string',
      filter: {
        // This lets us filter by raw AND pretty type vs enabling filterCellFiltered
        condition: (searchTerm, type) => {
          const exp = new RegExp(searchTerm, 'i');
          return exp.test(type) || exp.test(equipmentPrettyType(type));
        },
      },
      sortingAlgorithm: (a, b) => equipmentPrettyType(a).localeCompare(equipmentPrettyType(b)),
    }, {
      field: 'rcuName',
      displayName: 'RCU',
      type: 'string',
      enableHiding: false,
    }, {
      field: 'siteName',
      displayName: 'Site',
      enableHiding: false,
      type: 'string',
      sortingAlgorithm: sortBySelected,
      sort: {
        direction: uiGridConstants.DESC,
        priority: 0
      }
    }],
    data: vm.masterList
  };

  vm.equipmentGrid.appScopeProvider = vm;
  vm.equipmentGrid.onRegisterApi = (gridApi) => {
    vm.gridApi = gridApi;

    gridApi.selection.on.rowSelectionChanged(null, handleRowSelectionChange);
    gridApi.selection.on.rowSelectionChangedBatch(null, handleRowSelectionChange);

    function handleRowSelectionChange() {
      vm.selectedEquipment = vm.gridApi.selection.getSelectedRows();
    }
  };

  EquipmentInfoService.setNeed('admin_equipment');

  /**
   * function that's called onInit (angularJS lifecycle hook)
   *
   * @returns {undefined}
   */
  function initialize() {
    loadNodes();
    if (vm.editing) {
      setupGroups();
    }

    $scope.$watch(() => vm.updatedOrg, () => {
      const id = vm.updatedOrg;
      if (id) {
        GroupService.getGroupByOrg(id).then(({ body }) => {
          vm.groupList = [body];
        }).catch((error) => {
          growl.error(error, { title: 'Could not get groups for selected organization' });
        });
      }
    });
  }

  function setupGroups() {
    vm.selectedGroups = site.groups;
  }

  /**
   * Checks to see if selectedGroups is empty.
   *
   * @returns {number} - returns size of selectedGroups. Caller checks truthiness of size.
   */
  vm.validateGroups = () => _.size(GroupsButtonService.cache.selectedGroupsTree);

  /**
   * calls when the save button is pressed on the modal. Closes the modal
   *
   * @returns {undefined}
   */
  vm.save = () => {
    const updatedSite = getSite();
    const { border, latitude, longitude } = updatedSite;
    if (border && latitude && longitude) {
      $uibModalInstance.close(_.assign(site, updatedSite)); // Close modal and save site
    } else {
      growl.error('Please draw a polygon for the site boundary.');
    }
  };

  /**
   * Calls when you hit the cancel or X on the modal
   *
   * @returns {undefined}
   */
  vm.dismiss = function dismiss() {
    $uibModalInstance.dismiss('closed');
  };

  /**
   * calls when the save button is pressed on the modal. Closes the modal
   *
   * @returns {undefined}
   */
  function getSite() {
    const {
      siteName: name,
      siteRegion: region,
      siteLat: latitude,
      siteLng: longitude,
      siteBorder: border,
      jsonSiteInfo,
      updatedOrg: org
    } = vm;
    const isRcu = ({ equipmentType }) => equipmentType === 'RCU';
    return {
      name,
      region,
      latitude,
      longitude,
      border,
      info: angular.fromJson(jsonSiteInfo),
      groups: GroupsButtonService.rebuildSelectedGroups(),
      equipment: _.map(_.reject(vm.selectedEquipment, isRcu), 'id'),
      rcus: _.map(_.filter(vm.selectedEquipment, isRcu), 'id'),
      org
    };
  }

  async function loadNodes() {
    const sites = _.keyBy(await SiteService.get(), 'id');

    const rcus = await RcuInfoService.getAllRcus()
      .then((res) => {
        res.forEach((rcu) => {
          if (rcu.site) {
            rcu.siteName = _.get(sites, [rcu.site.id, 'name'], '[FORBIDDEN]');
          }
          rcu.rcuName = rcu.name; // fill masterList with rcu names ALONGSIDE rcu
          vm.masterList.push(rcu);
        });
        return _.keyBy(res, 'id');
      });

    const equipment = await EquipmentInfoService.getAllEquipments();
    equipment.forEach((eq) => {
      if (eq.site) {
        eq.siteName = _.get(sites, [eq.site, 'name'], '[FORBIDDEN]');
      }
      eq.rcuName = _.get(rcus, [eq.rcu, 'name'], '[FORBIDDEN]');
      vm.masterList.push(eq);
    });

    // Get equipment that is associated with this site and start it off as selected.
    vm.gridApi.grid.modifyRows(vm.masterList);
    vm.selectedEquipment.forEach((eq) => {
      const indexOfSelectedEq = vm.masterList.map(({ id }) => id).indexOf(eq.id);
      vm.gridApi.selection.selectRow(vm.masterList[indexOfSelectedEq]);
    });
    vm.gridApi.core.notifyDataChange(uiGridConstants.dataChange.ALL);
  }

  /**
   * @param {string} first - The item the sorter is currently pointing to
   * @param {string} second - The item the after what the sorter is pointing to
   * @param {object} firstRow - firstRow
   * @returns {number} -1, 0, or 1 depending on how first and second are to be re-ordered
   */
  function sortBySelected(first, second, firstRow) {
    const { siteName } = firstRow.grid.appScope;
    const firstIsSelected = first === siteName;
    const secondIsSelected = second === siteName;
    if (firstIsSelected && secondIsSelected) {
      return 0;
    } else if (!firstIsSelected && !secondIsSelected) {
      if (first && !second) {
        return -1;
      }
      if (!first && second) {
        return 1;
      }
      return 0;
    } else if (firstIsSelected) {
      return 1;
    }
    return -1;
  }
}

export default {
  name: 'siteManagementModal',
  template,
  controller,
  controllerAs: 'modal',
  bindings: {
    site: '<'
  }
};
